import React, { useEffect } from "react";
import BRAND from "@/config/brand";

/**
 * <Seo /> — Per-page SEO + AEO metadata.
 * React 19 automatically hoists <title>, <meta>, <link>, and <script> to <head>.
 *
 * Props:
 *   title:        page-specific title
 *   description:  ~155 char meta description that answers "what is this page?"
 *   canonical:    absolute canonical URL (defaults to current location)
 *   image:        absolute OG image URL
 *   type:         og:type (website | article | event ...)
 *   jsonLd:       object OR array of JSON-LD structured data objects
 *   keywords:     optional keywords list (mostly ignored by Google, useful for some AEO crawlers)
 */
const SITE_NAME = BRAND.name;
const DEFAULT_DESCRIPTION = BRAND.description;
const DEFAULT_IMAGE = "/og-image.png";

function toAbsolute(url) {
  if (!url) return url;
  if (url.startsWith("http")) return url;
  if (typeof window !== "undefined") return `${window.location.origin}${url}`;
  return url;
}

function siteOrigin() {
  if (typeof window !== "undefined") return window.location.origin;
  return "";
}

export default function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical,
  image = DEFAULT_IMAGE,
  type = "website",
  jsonLd,
  keywords,
  noindex = false,
}) {
  const fullTitle = title ? `${title} · ${SITE_NAME}` : `${SITE_NAME} — ${BRAND.tagline}`;
  const url =
    canonical ||
    (typeof window !== "undefined" ? window.location.href : siteOrigin());
  const absoluteImage = toAbsolute(image);
  const scripts = jsonLd
    ? Array.isArray(jsonLd) ? jsonLd : [jsonLd]
    : [];

  // Update (rather than duplicate) the canonical <link> tag that the
  // pre-hydration script placed in index.html. React 19's metadata hoisting
  // does NOT deduplicate <link rel="canonical">, so we mutate the existing
  // #canonical-tag element to guarantee exactly one canonical per page.
  useEffect(() => {
    if (typeof document === "undefined") return;
    // Strip the query-string from canonicals so /register?role=business
    // canonicalizes to /register (avoiding duplicate content signals).
    let canonicalUrl = url;
    try {
      const u = new URL(url);
      u.search = "";
      canonicalUrl = u.toString();
    } catch { /* noop */ }
    let el = document.getElementById("canonical-tag");
    if (!el) {
      el = document.createElement("link");
      el.setAttribute("rel", "canonical");
      el.setAttribute("id", "canonical-tag");
      document.head.appendChild(el);
    }
    el.setAttribute("href", canonicalUrl);
    // Remove any additional canonicals React may have inserted before us.
    document.querySelectorAll('link[rel="canonical"]').forEach(l => {
      if (l !== el) l.parentNode?.removeChild(l);
    });
  }, [url]);

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {/* Canonical is managed via useEffect above (single-tag guarantee). */}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteImage} />

      {/* AEO / LLM hints */}
      <meta name="author" content={SITE_NAME} />
      <meta name="publisher" content={SITE_NAME} />

      {/* Structured data */}
      {scripts.map((data, i) => (
        <script
          key={i}
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
        />
      ))}
    </>
  );
}

/* -------- Reusable JSON-LD builders -------- */

export const organizationJsonLd = (origin = siteOrigin()) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "ADN Local",
  alternateName: BRAND.name,
  url: origin || BRAND.origin,
  logo: `${origin || BRAND.origin}/favicon.svg`,
  description:
    "ADN Local runs FanFest — Jacksonville's local watch-party sweepstakes platform connecting fans, sponsors, and local businesses.",
  slogan: BRAND.motto,
  areaServed: { "@type": "City", name: BRAND.city, "@id": "https://en.wikipedia.org/wiki/Jacksonville,_Florida" },
  sameAs: Object.values(BRAND.social).filter(Boolean),
});

export const websiteJsonLd = (origin = siteOrigin()) => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: origin,
  potentialAction: {
    "@type": "SearchAction",
    target: `${origin}/directory?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
});

export const eventJsonLd = (origin = siteOrigin()) => ({
  "@context": "https://schema.org",
  "@type": "Event",
  name: "ADN Local FanFest Jacksonville — 2026 World Cup Watch Party",
  description:
    "Free citywide sweepstakes and watch-party festival across Jacksonville, FL. Fans register free, check in at Official Watch Party Partners, collect Fan Passport stamps, and enter to win daily prizes plus a Grand Prize — 6 days / 5 nights in Cancun for two with roundtrip airfare and $500 cash — drawn live after the World Cup Final.",
  startDate: "2026-07-06T00:00:00-04:00",
  endDate: "2026-07-19T22:00:00-04:00",
  eventStatus: "https://schema.org/EventScheduled",
  eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
  location: {
    "@type": "Place",
    name: "Jacksonville, Florida",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Jacksonville",
      addressRegion: "FL",
      addressCountry: "US",
    },
  },
  organizer: { "@type": "Organization", name: "ADN Local", url: origin },
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
    availability: "https://schema.org/InStock",
    url: `${origin}/register`,
    validFrom: "2026-06-01T00:00:00-04:00",
  },
  image: [DEFAULT_IMAGE],
  url: `${origin}/fanfest`,
});

export const localBusinessJsonLd = (biz, origin = siteOrigin()) => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${origin}/business/${biz.id}`,
  name: biz.business_name,
  description: biz.description,
  image: (biz.photos && biz.photos[0]) || undefined,
  telephone: biz.phone,
  url: biz.website || `${origin}/business/${biz.id}`,
  address: {
    "@type": "PostalAddress",
    streetAddress: biz.address,
    addressLocality: biz.city || "Jacksonville",
    addressRegion: biz.state || "FL",
    postalCode: biz.zip,
    addressCountry: "US",
  },
  ...(biz.latitude && biz.longitude
    ? { geo: { "@type": "GeoCoordinates", latitude: biz.latitude, longitude: biz.longitude } }
    : {}),
  ...(biz.hours ? { openingHours: biz.hours } : {}),
});

export const faqJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
});

export const howToJsonLd = (origin = siteOrigin()) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "How to enter ADN Local FanFest and win prizes",
  description:
    "Three steps to enter the ADN Local FanFest sweepstakes and qualify for daily prizes plus the Grand Prize Cancun trip.",
  totalTime: "PT2M",
  supply: [{ "@type": "HowToSupply", name: "Smartphone with camera" }],
  step: [
    { "@type": "HowToStep", position: 1, name: "Register free", url: `${origin}/register`,
      text: "Fill out the free entry form with your name, email, phone, ZIP, and date of birth. Must be 18+, one entry per family/household. No purchase necessary." },
    { "@type": "HowToStep", position: 2, name: "Check in at Watch Party Partners",
      text: "Between July 6–18, 2026, visit any Official Watch Party Partner in Jacksonville and scan the ADN QR code. Each check-in earns a Fan Passport stamp." },
    { "@type": "HowToStep", position: 3, name: "Qualify for the Grand Prize",
      text: "On July 19, 2026, check in at any Watch Party Partner between 12:00 PM and 2:45 PM EDT. The Grand Prize — 6 days / 5 nights in Cancun for two, roundtrip airfare included, plus $500 cash (ARV $2,999) — is drawn live after the World Cup Final." },
  ],
});

/* -------- Breadcrumb / ItemList / Speakable — AEO-critical helpers -------- */

/**
 * breadcrumbJsonLd — trail array [{ name, url }] rendered as a BreadcrumbList.
 * Google uses this for the breadcrumb display in SERP; AI engines use it to
 * understand the page's position in the site hierarchy.
 */
export const breadcrumbJsonLd = (trail, origin = siteOrigin()) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: trail.map((t, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: t.name,
    item: t.url ? (t.url.startsWith("http") ? t.url : `${origin}${t.url}`) : undefined,
  })),
});

/**
 * itemListJsonLd — a ranked collection (used on the Directory page so search
 * engines can surface a rich list-of-businesses result and AI engines can
 * answer "what bars are on ADN Local?" with a structured list).
 *
 * items = [{ name, url, description?, image? }]
 */
export const itemListJsonLd = (listName, items, origin = siteOrigin()) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: listName,
  numberOfItems: items.length,
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    url: it.url
      ? (it.url.startsWith("http") ? it.url : `${origin}${it.url}`)
      : undefined,
    name: it.name,
    ...(it.description ? { description: it.description } : {}),
    ...(it.image ? { image: it.image } : {}),
  })),
});

/**
 * speakableJsonLd — marks a page as suitable for voice / smart-speaker
 * summarisation (Google Assistant, Alexa, ChatGPT voice). Reads the elements
 * matching the given CSS selectors out loud.
 */
export const speakableJsonLd = (cssSelectors = [".speakable-title", ".speakable-summary"]) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  speakable: {
    "@type": "SpeakableSpecification",
    cssSelector: cssSelectors,
  },
});
